import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const KEY = "vixera_splash_shown_session";

/**
 * PWA launch splash. Renders once per full app load (SPA nav skipped).
 * Duration ≈ 1s total: 550ms in, 350ms hold, 550ms out.
 */
export default function Splash() {
  // Lazy initializer — read sessionStorage synchronously so we never render
  // the overlay for even a single frame when the flag is already set.
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(KEY) !== "1";
  });

  useEffect(() => {
    if (!show) return;
    sessionStorage.setItem(KEY, "1");
    const t = setTimeout(() => setShow(false), 1000);
    return () => clearTimeout(t);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          data-testid="vixera-splash"
          className="pointer-events-none fixed inset-0 z-[999] flex items-center justify-center bg-velvet-coal"
          aria-hidden="true"
        >
          {/* Radial glow behind the mark */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="absolute h-[60vmin] w-[60vmin] rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle, rgba(210,168,87,0.45) 0%, rgba(193,95,118,0.15) 45%, transparent 70%)",
            }}
          />

          {/* Wordmark */}
          <div className="relative flex flex-col items-center">
            <motion.svg
              width="90"
              height="90"
              viewBox="0 0 100 100"
              initial={{ opacity: 0, rotate: -6, scale: 0.9 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <defs>
                <linearGradient id="splashV" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F0C377" />
                  <stop offset="55%" stopColor="#D2A857" />
                  <stop offset="100%" stopColor="#C15F76" />
                </linearGradient>
              </defs>
              <motion.path
                d="M20 20 L50 82 L80 20"
                fill="none"
                stroke="url(#splashV)"
                strokeWidth={7}
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
              />
            </motion.svg>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 font-heading text-3xl font-light tracking-[0.35em] text-white"
            >
              VIXERA
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.35 }}
              className="mt-2 text-[10px] uppercase tracking-luxe text-velvet-gold"
            >
              Premium companionship
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
